<template>
  <Layout>
    <div class="pos-page">
      <PageHeader title="Point of Sale" :items="breadcrumbs" />

      <!-- Branch Indicator -->
      <div class="mb-2">
        <branch-indicator
          v-if="hasBranch || !isBranchLocked"
          :branch-name="activeBranchName"
          :is-locked="isBranchLocked"
          :branches="availableBranches"
          :selected-branch-id="activeBranchId"
          @select-branch="onSelectBranch"
        />
        <b-alert v-else show variant="warning" class="mb-0">
          <i class="bx bx-error-circle mr-1"></i>
          No branch assigned — contact your administrator.
        </b-alert>
      </div>

      <!-- Prompt admin to pick a branch when none selected -->
      <div v-if="!hasBranch && !isBranchLocked" class="text-center text-muted py-5">
        <i class="bx bx-buildings fa-3x mb-3 d-block"></i>
        Select a branch above to begin
      </div>

      <!-- Member Context Bar — Full Width Top -->
      <member-context-bar v-if="hasBranch" />

      <!-- Two-Panel Layout -->
      <b-row
        v-if="hasBranch"
        ref="twoPane"
        class="pos-two-pane mt-3"
        :style="paneHeight ? { height: paneHeight + 'px' } : null"
      >
        <!-- Product Grid (left) -->
        <b-col cols="12" lg="7" class="pos-two-pane__col">
          <!-- No member loaded -->
          <div v-if="!hasMember" class="text-center text-muted py-5">
            <i class="fas fa-qrcode fa-3x mb-3 d-block"></i>
            Scan a member's QR code or enter their member number to begin
          </div>

          <!-- Member inactive -->
          <div v-else-if="currentMember && !currentMember.membership_active" class="text-center text-muted py-5">
            Member is inactive — cannot create orders
          </div>

          <!-- Products loading -->
          <div v-else-if="productsLoading" class="pos-product-grid">
            <div
              v-for="n in 9"
              :key="'skeleton-' + n"
              class="pos-skeleton-card"
            ></div>
          </div>

          <!-- Products loaded -->
          <template v-else>
            <div class="pos-product-search mb-2">
              <b-input-group>
                <b-input-group-prepend is-text>
                  <i class="bx bx-search"></i>
                </b-input-group-prepend>
                <b-form-input
                  v-model="productSearch"
                  type="search"
                  placeholder="Search products by name or SKU"
                  autocomplete="off"
                />
                <b-input-group-append v-if="productSearch">
                  <b-button variant="outline-secondary" @click="productSearch = ''">
                    <i class="bx bx-x"></i>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>

            <div v-if="filteredProducts.length === 0" class="text-center text-muted py-5">
              No products match "{{ productSearch }}"
            </div>

            <div
              v-else
              ref="productGrid"
              class="pos-product-grid"
              tabindex="0"
              @mousedown="onGridMouseDown"
              @keydown="onGridKeydown"
            >
              <pos-product-card
                v-for="product in filteredProducts"
                :key="product.id"
                :product="product"
                @add-to-order="onAddToOrder"
                @view-details="onViewDetails"
              />
            </div>
          </template>
        </b-col>

        <!-- Order Panel (right) -->
        <b-col cols="12" lg="5" class="pos-two-pane__col">
          <!-- Confirmation State Overlay — replaces order panel when active -->
          <confirmation-state
            v-if="showConfirmation"
            :type="confirmationType"
            :order-summary="confirmationSummary"
            :error-message="confirmationErrorMessage"
            :error-details="confirmationErrorDetails"
            @dismissed="onConfirmationDismissed"
          />

          <!-- Normal Order Panel -->
          <order-panel
            v-else
            @confirm-order="onConfirmOrder"
          />
        </b-col>
      </b-row>
    </div>

    <!-- Product Detail Modal -->
    <product-detail-modal
      :product="selectedProduct"
      action-label="Add to Order"
      @add-product="onAddFromModal"
      @closed="selectedProduct = null"
    />
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import MemberContextBar from "@/components/pos/memberContextBar";
import PosProductCard from "@/components/pos/posProductCard";
import OrderPanel from "@/components/pos/orderPanel";
import ConfirmationState from "@/components/pos/confirmationState";
import ProductDetailModal from "@/components/shared/productDetailModal";
import BranchIndicator from "@/components/pos/branchIndicator";
import { posComputed, posMethods, adminComputed, adminMethods } from "@/state/helpers";

export default {
    page: {
        title: "Point of Sale",
    },
    components: { Layout, PageHeader, MemberContextBar, PosProductCard, OrderPanel, ConfirmationState, ProductDetailModal, BranchIndicator },
    data() {
        return {
            selectedProduct: null,
            paneHeight: null,
            productSearch: "",
        };
    },
    computed: {
        ...posComputed,
        ...adminComputed,
        activeBranchId() {
            if (this.user_role === 'budtender') {
                return this.core?.user_branch?.id || null;
            }
            return this.selected_branch_id > 0 ? this.selected_branch_id : null;
        },
        activeBranchName() {
            if (!this.activeBranchId || !this.core?.branch_config) return '';
            const branch = this.core.branch_config.find(b => b.id === parseInt(this.activeBranchId));
            return branch ? branch.name : '';
        },
        isBranchLocked() {
            return this.user_role === 'budtender';
        },
        hasBranch() {
            return !!this.activeBranchId;
        },
        availableBranches() {
            return this.core?.branch_config || [];
        },
        breadcrumbs() {
            return [
                { text: "leafSpot", href: "/" },
                { text: "Point of Sale", active: true },
            ];
        },
        showConfirmation() {
            return this.transactionState === 'confirmed' || this.showStockError;
        },
        showStockError() {
            return this.posError && this.posError.error === 'Insufficient stock' && this.transactionState === 'order-building';
        },
        confirmationType() {
            if (this.transactionState === 'confirmed') return 'success';
            if (this.showStockError) return 'error';
            return 'success';
        },
        confirmationSummary() {
            if (!this.posConfirmation) return {};
            return {
                memberName: this.posConfirmation.member_name || '',
                itemCount: this.posConfirmation.item_count || 0,
                total: this.posConfirmation.order_total || 0,
                paymentMethod: this.posConfirmation.payment_method || 'cash',
                branchName: this.activeBranchName,
            };
        },
        confirmationErrorMessage() {
            return this.posError?.error || 'Order failed';
        },
        confirmationErrorDetails() {
            if (!this.posError?.details) return [];
            return this.posError.details.map(d => {
                const product = this.posProducts.find(p => p.id === d.product_id);
                return {
                    ...d,
                    product_name: product ? product.name : `Product #${d.product_id}`,
                };
            });
        },
        filteredProducts() {
            const q = this.productSearch.trim().toLowerCase();
            if (!q) return this.posProducts;
            return this.posProducts.filter(p => {
                const name = (p.name || '').toLowerCase();
                const sku = (p.sku || '').toLowerCase();
                return name.includes(q) || sku.includes(q);
            });
        },
    },
    watch: {
        currentMember(newMember) {
            if (newMember && newMember.membership_active) {
                this.fetchPosProducts();
                this.fetchPendingOrders(this.activeBranchId);
            }
            this.$nextTick(this.updatePaneHeight);
        },
        hasBranch() {
            this.$nextTick(this.updatePaneHeight);
        },
        posProducts() {
            this.$nextTick(this.updatePaneHeight);
        },
        productsLoading() {
            this.$nextTick(this.updatePaneHeight);
        },
    },
    mounted() {
        this.updatePaneHeight();
        window.addEventListener('resize', this.updatePaneHeight);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updatePaneHeight);
    },
    beforeRouteLeave(to, from, next) {
        if (this.transactionState === 'order-building' && this.orderItems.length > 0) {
            const confirmed = window.confirm('Discard current order?');
            if (confirmed) {
                this.resetTransaction();
                next();
            } else {
                next(false);
            }
        } else {
            next();
        }
    },
    methods: {
        ...posMethods,
        ...adminMethods,
        onSelectBranch(branchId) {
            this.setSelectedBranch(branchId);
        },
        onAddToOrder(product) {
            this.addToOrder(product);
            this.$bvToast.toast(`${product.name} added`, {
                variant: "success",
                autoHideDelay: 2000,
                noCloseButton: true,
                toaster: "b-toaster-bottom-right",
            });
        },
        onConfirmOrder() {
            this.confirmOrder(this.activeBranchId).catch(() => {});
        },
        onConfirmationDismissed() {
            if (this.transactionState === 'confirmed') {
                this.resetTransaction();
            } else {
                this.$store.commit('pos/SET_ERROR', null);
            }
        },
        onViewDetails(product) {
            this.selectedProduct = product;
            this.$nextTick(() => {
                this.$bvModal.show('productDetailModal');
            });
        },
        onAddFromModal(product) {
            this.onAddToOrder(product);
            this.$bvModal.hide('productDetailModal');
        },
        onGridMouseDown(e) {
            const grid = this.$refs.productGrid;
            if (!grid) return;
            // Ignore drags that start on interactive elements inside cards.
            if (e.target.closest('button, a, input, .pos-product-card__action')) return;
            const start = { y: e.clientY, scrollTop: grid.scrollTop, moved: false };
            const onMove = (ev) => {
                const dy = ev.clientY - start.y;
                if (Math.abs(dy) > 4) start.moved = true;
                grid.scrollTop = start.scrollTop - dy;
            };
            const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                grid.style.cursor = '';
                if (start.moved) {
                    // Swallow the click that would otherwise fire on a card after a drag.
                    const swallow = (ev) => {
                        ev.stopPropagation();
                        ev.preventDefault();
                        window.removeEventListener('click', swallow, true);
                    };
                    window.addEventListener('click', swallow, true);
                }
            };
            grid.style.cursor = 'grabbing';
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        onGridKeydown(e) {
            const grid = this.$refs.productGrid;
            if (!grid) return;
            const step = 80;
            const page = Math.max(grid.clientHeight - 40, step);
            if (e.key === 'ArrowDown' || e.key === 'j') {
                grid.scrollTop += step;
            } else if (e.key === 'ArrowUp' || e.key === 'k') {
                grid.scrollTop -= step;
            } else if (e.key === 'PageDown' || e.key === ' ') {
                grid.scrollTop += page;
            } else if (e.key === 'PageUp') {
                grid.scrollTop -= page;
            } else if (e.key === 'Home') {
                grid.scrollTop = 0;
            } else if (e.key === 'End') {
                grid.scrollTop = grid.scrollHeight;
            } else {
                return;
            }
            e.preventDefault();
        },
        updatePaneHeight() {
            this.$nextTick(() => {
                if (window.innerWidth < 992) {
                    this.paneHeight = null;
                    return;
                }
                const ref = this.$refs.twoPane;
                const el = ref && (ref.$el || ref);
                if (!el || typeof el.getBoundingClientRect !== 'function') {
                    // Row not yet in the DOM (Layout still loading, no branch, etc.).
                    // Retry on the next animation frame.
                    if (!this._paneHeightRetry) {
                        this._paneHeightRetry = window.setTimeout(() => {
                            this._paneHeightRetry = null;
                            this.updatePaneHeight();
                        }, 100);
                    }
                    return;
                }
                const top = el.getBoundingClientRect().top;
                const footerH = 70;
                const available = window.innerHeight - top - footerH;
                this.paneHeight = Math.max(360, available);
            });
        },
    },
};
</script>

<style scoped lang="scss">
@import "~@/design/tokens/surfaces";
@import "~@/design/tokens/spacing";

// The row's height is set via JS (updatePaneHeight) so it matches whatever
// space is left below the breadcrumb / branch indicator / member-context-bar.
.pos-two-pane {
    min-height: 360px;
    // Critical: Bootstrap's .row sets flex-wrap: wrap, which makes
    // align-items: stretch only apply within a wrap line — so columns
    // overflow the row's height by their intrinsic content height,
    // pushing the bottom of the products grid (and its scrollbar) under
    // the footer. nowrap makes the columns honour the row's height.
    flex-wrap: nowrap;
}

.pos-two-pane__col {
    display: flex;
    flex-direction: column;
    min-height: 0;
    max-height: 100%;
    overflow: hidden;
}

.pos-product-grid {
    flex: 1;
    min-height: 0;
    // Hard cap on grid height so it can never overflow past the footer,
    // regardless of whether the JS-measured pane height is applied.
    max-height: 55vh;
    overflow-y: scroll; // "scroll" (not "auto") so the gutter is always reserved
    overflow-x: hidden;
    padding-right: 8px;
    cursor: grab;
    outline: none;

    &:focus-visible {
        box-shadow: inset 0 0 0 2px rgba(108, 99, 255, 0.4);
        border-radius: 4px;
    }

    // Force a visible scrollbar on macOS (where bars are otherwise hidden).
    &::-webkit-scrollbar {
        width: 10px;
    }
    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.05);
        border-radius: 5px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.3);
        border-radius: 5px;

        &:hover {
            background: rgba(0, 0, 0, 0.45);
        }
    }
    // Firefox
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.05);

    display: grid;
    // minmax(0, 1fr) — not plain 1fr — so cells can shrink below their
    // intrinsic content width. Otherwise long product names force columns
    // wider than the container and they slide under the order panel.
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: $space-3;

    @media (max-width: 991px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

.pos-skeleton-card {
    background: $surface-elevated;
    border-radius: 8px;
    min-height: 120px;
    animation: skeleton-pulse 1.5s infinite;
}

@keyframes skeleton-pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0.7; }
}

@media (max-width: 991px) {
    .pos-two-pane,
    .pos-two-pane__col {
        flex: none;
        display: block;
    }
    .pos-product-grid {
        flex: none;
        // Keep the grid scrollable on mobile too, just give it a bit more room.
        max-height: 65vh;
        padding-right: 4px;
        cursor: default;
    }
}
</style>
